import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { Plus, Search, X, Trash2 } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import { searchKey } from '../lib/utils';

const eur = (v) => `${Number(v || 0).toFixed(2).replace('.', ',')} €`;
const UNIT_LABEL = { kg: 'kg', g: 'g', l: 'l', cl: 'cl', ml: 'ml', unit: 'pièce' };
const UNITS_FOR = { kg: ['g', 'kg'], g: ['g', 'kg'], l: ['ml', 'cl', 'l'], cl: ['ml', 'cl', 'l'], ml: ['ml', 'cl', 'l'], unit: ['unit'] };
const FACTOR = { kg: 1, g: 0.001, l: 1, cl: 0.01, ml: 0.001, unit: 1 };

const lineCost = (line) => {
    const qty = parseFloat(line.quantity) || 0;
    return qty * (FACTOR[line.unit] / FACTOR[line.component_unit]) * parseFloat(line.component_unit_price || 0);
};

const apiError = (error) => {
    const data = error?.response?.data;
    if (!data) return 'Enregistrement impossible.';
    if (data.error) return data.error;
    return Object.values(data).flat().join(' ');
};

const EMPTY = { name: '', yield_quantity: '', unit: 'kg', lines: [] };

/** Editor for one preparation: its ingredients for one batch, and what that batch yields. */
const PreparationForm = ({ initial, ingredients, onClose, onSaved }) => {
    const [form, setForm] = useState(initial);
    const [search, setSearch] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const options = search
        ? ingredients.filter((i) => i.id !== form.id && !form.lines.some((l) => l.component === i.id)
            && searchKey(i.name).includes(searchKey(search))).slice(0, 10)
        : [];
    const batch = form.lines.reduce((total, line) => total + lineCost(line), 0);
    const yieldQty = parseFloat(form.yield_quantity);
    const price = yieldQty > 0 ? batch / yieldQty : null;

    const addLine = (ingredient) => {
        setForm({
            ...form,
            lines: [...form.lines, {
                component: ingredient.id,
                component_name: ingredient.name,
                component_unit: ingredient.unit,
                component_unit_price: ingredient.unit_price,
                quantity: '',
                unit: UNITS_FOR[ingredient.unit][0],
            }],
        });
        setSearch('');
    };
    const setLine = (index, patch) => setForm({ ...form, lines: form.lines.map((l, i) => (i === index ? { ...l, ...patch } : l)) });

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        const payload = {
            name: form.name.trim(),
            unit: form.unit,
            yield_quantity: form.yield_quantity,
            lines: form.lines.map((l) => ({ component: l.component, quantity: l.quantity, unit: l.unit })),
        };
        try {
            if (form.id) await axiosInstance.put(`preparations/${form.id}/`, payload);
            else await axiosInstance.post('preparations/', payload);
            onSaved();
        } catch (err) {
            setError(apiError(err));
            setSaving(false);
        }
    };

    return (
        <div className="sheet-overlay">
            <form onSubmit={submit} onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault(); }} className="sheet space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl">{form.id ? form.name : 'Nouvelle préparation'}</h2>
                    <button type="button" onClick={onClose} aria-label="Fermer" className="p-2 hover:bg-accent"><X className="h-4 w-4" /></button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                    <div>
                        <label htmlFor="prep-name" className="block text-sm font-medium mb-1">Nom</label>
                        <input id="prep-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Crème aux herbes" className="w-full px-3 py-2 border border-border bg-background" />
                    </div>
                    <div>
                        <label htmlFor="prep-yield" className="block text-sm font-medium mb-1">Une recette donne</label>
                        <div className="flex gap-2">
                            <input id="prep-yield" required type="number" min="0" step="any" value={form.yield_quantity} onChange={(e) => setForm({ ...form, yield_quantity: e.target.value })} placeholder="1,8" className="w-24 px-3 py-2 border border-border bg-background text-right" />
                            <select aria-label="Unité du rendement" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="px-2 py-2 border border-border bg-background">
                                <option value="kg">kg</option>
                                <option value="l">l</option>
                                <option value="unit">pièces</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div>
                    <h3 className="font-display font-bold text-base mb-2">Ingrédients pour une recette</h3>
                    <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input aria-label="Ajouter un ingrédient" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ajouter un ingrédient…" className="w-full pl-10 pr-3 py-2 border border-border bg-background" />
                        {options.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 panel max-h-56 overflow-auto">
                                {options.map((i) => (
                                    <button key={i.id} type="button" onClick={() => addLine(i)} className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex justify-between gap-3">
                                        <span>{i.name}{i.is_preparation && <span className="tag ml-2 text-good">préparation</span>}</span>
                                        <span className="figure-num text-muted-foreground whitespace-nowrap">{eur(i.unit_price)}/{UNIT_LABEL[i.unit]}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {form.lines.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-3 text-center">Aucun ingrédient pour l’instant.</p>
                    ) : (
                        <div className="divide-y divide-border border-y border-border">
                            {form.lines.map((line, index) => (
                                <div key={line.component} className="py-2 flex flex-wrap items-center gap-2">
                                    <span className="flex-1 min-w-[10rem] text-sm font-medium">{line.component_name}</span>
                                    <input aria-label={`Quantité de ${line.component_name}`} required type="number" min="0" step="any" value={line.quantity} onChange={(e) => setLine(index, { quantity: e.target.value })} className="w-24 px-2 py-1 border border-border bg-background text-right text-sm" />
                                    <select aria-label={`Unité de ${line.component_name}`} value={line.unit} onChange={(e) => setLine(index, { unit: e.target.value })} className="px-2 py-1 border border-border bg-background text-sm">
                                        {UNITS_FOR[line.component_unit].map((u) => <option key={u} value={u}>{UNIT_LABEL[u]}</option>)}
                                    </select>
                                    <span className="figure-num text-sm w-16 text-right">{eur(lineCost(line))}</span>
                                    <button type="button" aria-label={`Retirer ${line.component_name}`} onClick={() => setForm({ ...form, lines: form.lines.filter((_, i) => i !== index) })} className="p-1 text-primary hover:bg-accent"><X className="h-4 w-4" /></button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-4 border border-border bg-muted/30 p-4">
                    <div>
                        <div className="field-label">Coût de la recette</div>
                        <div className="figure-num text-xl mt-1">{eur(batch)}</div>
                    </div>
                    <div>
                        <div className="field-label">Prix calculé</div>
                        <div className="figure-num text-xl mt-1">{price == null ? '—' : `${eur(price)}/${UNIT_LABEL[form.unit]}`}</div>
                    </div>
                </div>

                {error && <p role="alert" className="text-sm font-medium text-bad">{error}</p>}

                <div className="flex gap-3">
                    <button type="button" onClick={onClose} className="btn-outline flex-1">Annuler</button>
                    <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Un instant…' : 'Enregistrer'}</button>
                </div>
            </form>
        </div>
    );
};

/**
 * Préparations: house-made sub-recipes (creams, sauces, batters). Each is priced from
 * its own recipe and can be used in dishes like any ingredient.
 */
const Preparations = () => {
    const [preparations, setPreparations] = useState([]);
    const [ingredients, setIngredients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState(null);

    const fetchAll = async () => {
        try {
            const [prep, ing] = await Promise.all([
                axiosInstance.get('preparations/'),
                axiosInstance.get('ingredients/?active=true'),
            ]);
            setPreparations(prep.data);
            setIngredients(ing.data);
        } catch (error) {
            console.error('Failed to fetch preparations:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = 'Préparations | Costy';
        fetchAll();
    }, []);

    const remove = async (preparation) => {
        if (!confirm(`Supprimer « ${preparation.name} » ?`)) return;
        try {
            await axiosInstance.delete(`preparations/${preparation.id}/`);
            fetchAll();
        } catch (error) {
            alert(apiError(error));
        }
    };

    const shown = preparations.filter((p) => searchKey(p.name).includes(searchKey(search)));

    return (
        <Layout>
            <div className="space-y-5">
                <PageHeader title="Préparations" subtitle="Crèmes, sauces, pâtes : changez la recette ici, tous les plats qui l’utilisent suivent.">
                    <button type="button" onClick={() => setEditing(EMPTY)} className="btn-primary">
                        <Plus className="h-4 w-4" />
                        Nouvelle préparation
                    </button>
                </PageHeader>

                {preparations.length > 5 && (
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input aria-label="Rechercher une préparation" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="w-full pl-10 pr-3 py-2 border border-border bg-background" />
                    </div>
                )}

                {loading ? (
                    <p className="text-sm text-muted-foreground">Chargement…</p>
                ) : shown.length === 0 ? (
                    <div className="panel p-8 text-center text-sm text-muted-foreground">
                        Aucune préparation. Créez-en une pour ne plus recopier la même recette dans chaque plat.
                    </div>
                ) : (
                    <div className="menu-list">
                        {shown.map((p) => (
                            <div key={p.id} className="flex items-center gap-3 px-4 py-3 border-t border-border first:border-t-0">
                                <button type="button" onClick={() => setEditing({ ...p, yield_quantity: parseFloat(p.yield_quantity), lines: p.lines.map((l) => ({ ...l, quantity: parseFloat(l.quantity) })) })} className="flex-1 min-w-0 text-left group">
                                    <span className="block font-bold text-[.9375rem] group-hover:underline">{p.name}</span>
                                    <span className="block text-sm text-muted-foreground">
                                        {eur(p.batch_cost)} pour {String(parseFloat(p.yield_quantity)).replace('.', ',')} {p.unit === 'unit' ? 'pièces' : p.unit},{' '}
                                        {p.used_in_dishes.length === 0 ? 'pas encore utilisée' : `${p.used_in_dishes.length} plat${p.used_in_dishes.length > 1 ? 's' : ''}`}
                                    </span>
                                </button>
                                <span className="price text-base whitespace-nowrap">{eur(p.unit_price)}<span className="text-xs text-muted-foreground"> /{UNIT_LABEL[p.unit]}</span></span>
                                <button type="button" aria-label={`Supprimer ${p.name}`} onClick={() => remove(p)} className="p-1.5 text-bad hover:bg-accent rounded-full"><Trash2 className="h-4 w-4" /></button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {editing && (
                <PreparationForm
                    initial={editing}
                    ingredients={ingredients}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); fetchAll(); }}
                />
            )}
        </Layout>
    );
};

export default Preparations;
